import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSmartphone, FiAperture, FiFolder, FiStar, FiUsers, FiVideo } = FiIcons;

const features = [
  {
    icon: FiSmartphone,
    title: "Zero Friction",
    desc: "No apps to download. Guests scan and upload directly from their mobile browser."
  },
  {
    icon: FiVideo,
    title: "HD Photos & Videos",
    desc: "Collect high-resolution memories without the compression of social media or messaging apps."
  },
  {
    icon: FiAperture,
    title: "Custom QR Branding",
    desc: "Generate beautiful QR codes that match your event's theme and style."
  },
  {
    icon: FiFolder,
    title: "Instant Organization",
    desc: "All memories are automatically sorted by time and guest, ready to download in one click."
  },
  {
    icon: FiUsers,
    title: "Private Community",
    desc: "A secure, private space for your guests to share their unique perspectives."
  }
];

export default function Features() {
  return (
    <section id="features" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-sm font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">Why Kuhako</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 tracking-tight leading-tight">
              A gallery for your <br />
              <span className="text-slate-400">once-in-a-lifetime</span> moments.
            </h3>
            
            <div className="grid sm:grid-cols-1 gap-8">
              {features.map((item, i) => (
                <div key={i} className="flex items-start gap-5 group">
                  <div className="mt-1 w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0 border border-slate-100 group-hover:scale-110 transition-transform duration-300">
                    <SafeIcon icon={item.icon} className="w-6 h-6 text-slate-700" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900">{item.title}</h4>
                    <p className="text-slate-500 mt-1 leading-relaxed font-light">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Contextual Visuals */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-[2.5rem] p-4 shadow-2xl shadow-slate-200/60 border border-slate-100"
            >
              <div className="aspect-[3/4] rounded-[2rem] overflow-hidden relative group">
                <img 
                  src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&q=80&w=800" 
                  alt="Concert Crowd" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full border-2 border-white overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=100" alt="Avatar" />
                    </div>
                    <p className="text-white font-medium">"The energy was insane! 🎸"</p>
                  </div>
                  <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ duration: 2, ease: "easeInOut" }}
                      className="h-full bg-indigo-400"
                    />
                  </div>
                  <p className="text-white/60 text-[10px] mt-2 font-bold tracking-widest uppercase">Live Uploading...</p>
                </div>
              </div>
            </motion.div>
            
            {/* Floating Badge */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              className="absolute -right-6 top-1/4 glass-panel p-4 rounded-2xl shadow-xl hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <SafeIcon icon={FiStar} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900">Premium Gallery</p>
                  <p className="text-[10px] text-slate-500">Shared by 142 guests</p>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}